import React, { useCallback, useMemo, useState } from 'react';
import {
    Platform,
    type StyleProp,
    TextInput,
    type TextStyle,
} from 'react-native';
import { Text, View, XStack, YStack } from 'smoothie';

import { runSimulation } from '../engine';
import { PRESETS } from '../presets';
import type {
    MoneyMapSimulation,
    SimulationParams,
    SimulationResult,
} from '../types';
import { validateMoneyMap } from '../validation';
import { SimulationResultsTable } from './SimulationResultsTable';

function getNextMonthStart(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 1);
}

function formatDateForInput(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
}

function parseDateFromInput(value: string): Date {
    const [y, m] = value.split('-').map(Number);
    return new Date(y ?? 2026, (m ?? 1) - 1, 1);
}

function formatCentsDisplay(cents: number): string {
    const abs = Math.abs(cents);
    const dollars = (abs / 100).toFixed(2);
    const formatted = Number(dollars).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    return cents < 0 ? `-$${formatted}` : `$${formatted}`;
}

interface SimulatorUIProps {
    initialJson?: string;
}

export const SimulatorUI = ({ initialJson }: SimulatorUIProps) => {
    const defaultJson =
        initialJson ?? JSON.stringify(PRESETS[0]?.moneyMap, null, 2);
    const hasInitialJson = initialJson != null;

    const [selectedPreset, setSelectedPreset] = useState(
        hasInitialJson ? -1 : 0,
    );
    const [jsonText, setJsonText] = useState(defaultJson);
    const [startDate, setStartDate] = useState(
        formatDateForInput(getNextMonthStart()),
    );
    const [numMonths, setNumMonths] = useState('36');
    const [depositApy, setDepositApy] = useState('4');
    const [investmentReturn, setInvestmentReturn] = useState('7');
    const [simulationResult, setSimulationResult] =
        useState<SimulationResult | null>(null);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [isCustom, setIsCustom] = useState(false);

    const handlePresetChange = useCallback((index: number) => {
        const preset = PRESETS[index];
        if (preset) {
            setSelectedPreset(index);
            setJsonText(JSON.stringify(preset.moneyMap, null, 2));
            setValidationErrors([]);
            setSimulationResult(null);
            setIsCustom(false);
        }
    }, []);

    const handleLoadInitial = useCallback(() => {
        setJsonText(defaultJson);
        setSelectedPreset(hasInitialJson ? -1 : 0);
        setValidationErrors([]);
        setSimulationResult(null);
        setIsCustom(false);
    }, [defaultJson, hasInitialJson]);

    const handleJsonChange = useCallback((text: string) => {
        setJsonText(text);
        setIsCustom(true);
        setValidationErrors([]);
    }, []);

    const handleValidate = useCallback(() => {
        const result = validateMoneyMap(jsonText);
        if (result.valid) {
            setValidationErrors([]);
            return true;
        } else {
            setValidationErrors(result.errors);
            setSimulationResult(null);
            return false;
        }
    }, [jsonText]);

    const handleRunSimulation = useCallback(() => {
        const validationResult = validateMoneyMap(jsonText);
        if (!validationResult.valid) {
            setValidationErrors(validationResult.errors);
            setSimulationResult(null);
            return;
        }

        setValidationErrors([]);

        const moneyMap: MoneyMapSimulation = JSON.parse(
            jsonText,
        ) as MoneyMapSimulation;
        const params: SimulationParams = {
            startDate: parseDateFromInput(startDate),
            numMonths: Math.max(
                1,
                Math.min(120, parseInt(numMonths, 10) || 36),
            ),
            depositApyPercent: parseFloat(depositApy) || 4,
            investmentReturnPercent: parseFloat(investmentReturn) || 7,
        };

        setSimulationResult(runSimulation(moneyMap, params));
    }, [jsonText, startDate, numMonths, depositApy, investmentReturn]);

    const handleFormatJson = useCallback(() => {
        try {
            const parsed = JSON.parse(jsonText) as unknown;
            setJsonText(JSON.stringify(parsed, null, 2));
        } catch {
            // invalid JSON, leave as-is
        }
    }, [jsonText]);

    const accountSummary = useMemo(() => {
        if (!simulationResult) return null;
        const firstMonth = simulationResult.months[0];
        const lastMonth =
            simulationResult.months[simulationResult.months.length - 1];
        if (!firstMonth || !lastMonth) return null;

        return simulationResult.moneyMap.accounts.map((a) => {
            const start = firstMonth.balancesBefore[a.id] ?? 0;
            const end = lastMonth.balancesAfter[a.id] ?? 0;
            const change = end - start;

            let completionDate: Date | null = null;
            let totalInterestPaid = 0;
            let totalPrincipalPaid = 0;
            let totalFromMinimums = 0;
            let totalFromGoals = 0;

            if (a.type === 'DEBT') {
                for (const month of simulationResult.months) {
                    totalInterestPaid += month.interestApplied[a.id] ?? 0;
                    totalFromMinimums +=
                        month.minimumPaymentsApplied[a.id] ?? 0;
                    totalFromGoals += month.transfers
                        .filter((t) => t.accountId === a.id)
                        .reduce((sum, t) => sum + t.amountCents, 0);

                    if (
                        completionDate == null &&
                        (month.balancesAfter[a.id] ?? 0) <= 0
                    ) {
                        completionDate = month.date;
                    }
                }
                totalPrincipalPaid = Math.max(start - end, 0);
            } else {
                for (const month of simulationResult.months) {
                    const rules = simulationResult.moneyMap.ruleSets.flatMap(
                        (rs) => rs.rules,
                    );
                    const targetingRules = rules.filter(
                        (r) => r.accountId === a.id,
                    );

                    const allGoalsMet = targetingRules.every((rule) => {
                        if (rule.allocationType === 'MINIMUM_BALANCE') {
                            return (
                                (month.balancesAfter[a.id] ?? 0) >=
                                rule.targetBalanceCents
                            );
                        }
                        return false;
                    });

                    if (
                        completionDate == null &&
                        targetingRules.length > 0 &&
                        targetingRules.some(
                            (r) => r.allocationType === 'MINIMUM_BALANCE',
                        ) &&
                        allGoalsMet
                    ) {
                        completionDate = month.date;
                    }
                }
            }

            return {
                name: a.name,
                type: a.type,
                start,
                end,
                change,
                completionDate,
                totalInterestPaid,
                totalPrincipalPaid,
                totalFromMinimums,
                totalFromGoals,
            };
        });
    }, [simulationResult]);

    return (
        <YStack gap={24} width="100%">
            {/* Top controls row */}
            <XStack gap={24} flexWrap="wrap">
                <YStack
                    gap={12}
                    padding={16}
                    borderWidth={1}
                    borderColor="#e5e7eb"
                    borderRadius={12}
                    minWidth={320}
                    flex={1}
                >
                    <Text fontSize={16} fontWeight="700">
                        Simulation Parameters
                    </Text>

                    <XStack gap={16} flexWrap="wrap">
                        <YStack gap={4} flex={1} minWidth={140}>
                            <Text fontSize={12} color="$gray1000">
                                Start Date
                            </Text>
                            {Platform.OS === 'web' ? (
                                <input
                                    type="month"
                                    value={startDate}
                                    onChange={(e) => {
                                        setStartDate(e.target.value);
                                    }}
                                    style={webInputStyle}
                                />
                            ) : (
                                <TextInput
                                    style={nativeInputStyle}
                                    value={startDate}
                                    onChangeText={setStartDate}
                                    placeholder="YYYY-MM"
                                />
                            )}
                        </YStack>

                        <YStack gap={4} flex={1} minWidth={140}>
                            <Text fontSize={12} color="$gray1000">
                                Months to Simulate
                            </Text>
                            <TextInput
                                style={nativeInputStyle}
                                value={numMonths}
                                onChangeText={setNumMonths}
                                keyboardType="numeric"
                                placeholder="36"
                            />
                        </YStack>

                        <YStack gap={4} flex={1} minWidth={140}>
                            <Text fontSize={12} color="$gray1000">
                                Deposit APY (%)
                            </Text>
                            <TextInput
                                style={nativeInputStyle}
                                value={depositApy}
                                onChangeText={setDepositApy}
                                keyboardType="numeric"
                                placeholder="4"
                            />
                        </YStack>

                        <YStack gap={4} flex={1} minWidth={140}>
                            <Text fontSize={12} color="$gray1000">
                                Investment Return (%)
                            </Text>
                            <TextInput
                                style={nativeInputStyle}
                                value={investmentReturn}
                                onChangeText={setInvestmentReturn}
                                keyboardType="numeric"
                                placeholder="7"
                            />
                        </YStack>
                    </XStack>
                </YStack>
            </XStack>

            {/* Preset selector + JSON editor */}
            <XStack gap={24} flexWrap="wrap">
                <YStack gap={12} minWidth={280} width={280}>
                    <Text fontSize={16} fontWeight="700">
                        Presets
                    </Text>
                    {hasInitialJson && (
                        <View
                            onPress={handleLoadInitial}
                            cursor="pointer"
                            padding={12}
                            borderRadius={8}
                            borderWidth={1}
                            borderColor={
                                !isCustom && selectedPreset === -1
                                    ? '#3b82f6'
                                    : '#e5e7eb'
                            }
                            backgroundColor={
                                !isCustom && selectedPreset === -1
                                    ? '#eff6ff'
                                    : '$white'
                            }
                            hoverStyle={{
                                backgroundColor: '#f9fafb',
                            }}
                        >
                            <Text
                                fontSize={13}
                                fontWeight="600"
                                color={
                                    !isCustom && selectedPreset === -1
                                        ? '#1d4ed8'
                                        : '$black'
                                }
                            >
                                Current Money Map
                            </Text>
                            <Text fontSize={11} color="$gray1000" marginTop={2}>
                                Use the currently loaded money map
                            </Text>
                        </View>
                    )}
                    {PRESETS.map((preset, i) => (
                        <View
                            key={preset.label}
                            onPress={() => {
                                handlePresetChange(i);
                            }}
                            cursor="pointer"
                            padding={12}
                            borderRadius={8}
                            borderWidth={1}
                            borderColor={
                                !isCustom && selectedPreset === i
                                    ? '#3b82f6'
                                    : '#e5e7eb'
                            }
                            backgroundColor={
                                !isCustom && selectedPreset === i
                                    ? '#eff6ff'
                                    : '$white'
                            }
                            hoverStyle={{
                                backgroundColor: '#f9fafb',
                            }}
                        >
                            <Text
                                fontSize={13}
                                fontWeight="600"
                                color={
                                    !isCustom && selectedPreset === i
                                        ? '#1d4ed8'
                                        : '$black'
                                }
                            >
                                {preset.label}
                            </Text>
                            <Text fontSize={11} color="$gray1000" marginTop={2}>
                                {preset.description}
                            </Text>
                        </View>
                    ))}
                    {isCustom && (
                        <View
                            padding={12}
                            borderRadius={8}
                            borderWidth={1}
                            borderColor="#f59e0b"
                            backgroundColor="#fffbeb"
                        >
                            <Text
                                fontSize={13}
                                fontWeight="600"
                                color="#92400e"
                            >
                                Custom (edited)
                            </Text>
                        </View>
                    )}
                </YStack>

                {/* JSON editor */}
                <YStack gap={8} flex={1} minWidth={400}>
                    <XStack justifyContent="space-between" alignItems="center">
                        <Text fontSize={16} fontWeight="700">
                            Money Map JSON
                        </Text>
                        <XStack gap={8}>
                            <View
                                onPress={handleFormatJson}
                                cursor="pointer"
                                paddingHorizontal={12}
                                paddingVertical={6}
                                borderRadius={6}
                                backgroundColor="#f3f4f6"
                                hoverStyle={{
                                    backgroundColor: '#e5e7eb',
                                }}
                            >
                                <Text fontSize={12} fontWeight="600">
                                    Format
                                </Text>
                            </View>
                            <View
                                onPress={handleValidate}
                                cursor="pointer"
                                paddingHorizontal={12}
                                paddingVertical={6}
                                borderRadius={6}
                                backgroundColor="#dbeafe"
                                hoverStyle={{
                                    backgroundColor: '#bfdbfe',
                                }}
                            >
                                <Text
                                    fontSize={12}
                                    fontWeight="600"
                                    color="#1d4ed8"
                                >
                                    Validate
                                </Text>
                            </View>
                        </XStack>
                    </XStack>
                    <TextInput
                        style={jsonEditorStyle}
                        value={jsonText}
                        onChangeText={handleJsonChange}
                        multiline
                        autoCorrect={false}
                        autoCapitalize="none"
                    />

                    {validationErrors.length > 0 && (
                        <YStack
                            padding={12}
                            borderRadius={8}
                            backgroundColor="#fef2f2"
                            borderWidth={1}
                            borderColor="#fecaca"
                            gap={4}
                        >
                            <Text
                                fontSize={13}
                                fontWeight="700"
                                color="#dc2626"
                            >
                                Validation Errors
                            </Text>
                            {validationErrors.map((err, i) => (
                                <Text
                                    key={i}
                                    fontSize={12}
                                    color="#991b1b"
                                    fontFamily="monospace"
                                >
                                    {err}
                                </Text>
                            ))}
                        </YStack>
                    )}

                    {validationErrors.length === 0 && jsonText.length > 0 && (
                        <Text fontSize={12} color="#16a34a">
                            JSON is valid
                        </Text>
                    )}
                </YStack>
            </XStack>

            {/* Run button */}
            <View
                onPress={handleRunSimulation}
                cursor="pointer"
                paddingHorizontal={24}
                paddingVertical={12}
                borderRadius={8}
                backgroundColor="#2563eb"
                alignSelf="flex-start"
                hoverStyle={{ backgroundColor: '#1d4ed8' }}
            >
                <Text fontSize={14} fontWeight="700" color="$white">
                    Run Simulation
                </Text>
            </View>

            {/* Account summary */}
            {accountSummary && (
                <YStack gap={8}>
                    <Text fontSize={18} fontWeight="700">
                        Account Summary
                    </Text>
                    <XStack gap={12} flexWrap="wrap">
                        {accountSummary.map((a) => (
                            <YStack
                                key={a.name}
                                padding={12}
                                borderRadius={8}
                                borderWidth={1}
                                borderColor="#e5e7eb"
                                minWidth={180}
                                gap={4}
                            >
                                <Text fontSize={13} fontWeight="600">
                                    {a.name}
                                </Text>
                                <Text fontSize={11} color="$gray1000">
                                    {a.type}
                                </Text>
                                <XStack
                                    justifyContent="space-between"
                                    marginTop={4}
                                >
                                    <YStack>
                                        <Text fontSize={10} color="$gray1000">
                                            Start
                                        </Text>
                                        <Text
                                            fontSize={13}
                                            fontFamily="monospace"
                                        >
                                            {formatCentsDisplay(a.start)}
                                        </Text>
                                    </YStack>
                                    <YStack alignItems="flex-end">
                                        <Text fontSize={10} color="$gray1000">
                                            End
                                        </Text>
                                        <Text
                                            fontSize={13}
                                            fontFamily="monospace"
                                            fontWeight="700"
                                        >
                                            {formatCentsDisplay(a.end)}
                                        </Text>
                                    </YStack>
                                </XStack>
                                <Text
                                    fontSize={12}
                                    fontWeight="600"
                                    color={
                                        a.type === 'DEBT'
                                            ? a.change <= 0
                                                ? '#16a34a'
                                                : '#dc2626'
                                            : a.change >= 0
                                              ? '#16a34a'
                                              : '#dc2626'
                                    }
                                    fontFamily="monospace"
                                >
                                    {a.change >= 0 ? '+' : ''}
                                    {formatCentsDisplay(a.change)}
                                </Text>
                                {a.completionDate != null && (
                                    <XStack
                                        marginTop={4}
                                        paddingTop={4}
                                        borderTopWidth={1}
                                        borderColor="#e5e7eb"
                                        alignItems="center"
                                        gap={4}
                                    >
                                        <Text fontSize={10} color="$gray1000">
                                            {a.type === 'DEBT'
                                                ? 'Paid off'
                                                : 'Goal met'}
                                        </Text>
                                        <Text
                                            fontSize={12}
                                            fontWeight="700"
                                            color="#7c3aed"
                                        >
                                            {a.completionDate.toLocaleDateString(
                                                'en-US',
                                                {
                                                    month: 'short',
                                                    year: 'numeric',
                                                },
                                            )}
                                        </Text>
                                    </XStack>
                                )}
                                {a.type === 'DEBT' &&
                                    (a.totalInterestPaid > 0 ||
                                        a.totalPrincipalPaid > 0) && (
                                        <YStack
                                            marginTop={4}
                                            paddingTop={4}
                                            borderTopWidth={1}
                                            borderColor="#e5e7eb"
                                            gap={2}
                                        >
                                            <XStack justifyContent="space-between">
                                                <Text
                                                    fontSize={10}
                                                    color="$gray1000"
                                                >
                                                    Principal paid
                                                </Text>
                                                <Text
                                                    fontSize={11}
                                                    fontFamily="monospace"
                                                    color="#16a34a"
                                                >
                                                    {formatCentsDisplay(
                                                        a.totalPrincipalPaid,
                                                    )}
                                                </Text>
                                            </XStack>
                                            <XStack justifyContent="space-between">
                                                <Text
                                                    fontSize={10}
                                                    color="$gray1000"
                                                >
                                                    Interest paid
                                                </Text>
                                                <Text
                                                    fontSize={11}
                                                    fontFamily="monospace"
                                                    color="#dc2626"
                                                >
                                                    {formatCentsDisplay(
                                                        a.totalInterestPaid,
                                                    )}
                                                </Text>
                                            </XStack>
                                            <XStack
                                                justifyContent="space-between"
                                                paddingTop={2}
                                                borderTopWidth={1}
                                                borderColor="#f3f4f6"
                                            >
                                                <Text
                                                    fontSize={10}
                                                    color="$gray1000"
                                                    fontWeight="600"
                                                >
                                                    Total paid
                                                </Text>
                                                <Text
                                                    fontSize={11}
                                                    fontFamily="monospace"
                                                    fontWeight="600"
                                                >
                                                    {formatCentsDisplay(
                                                        a.totalPrincipalPaid +
                                                            a.totalInterestPaid,
                                                    )}
                                                </Text>
                                            </XStack>
                                            {(a.totalFromMinimums > 0 ||
                                                a.totalFromGoals > 0) && (
                                                <YStack
                                                    marginTop={4}
                                                    paddingTop={4}
                                                    borderTopWidth={1}
                                                    borderColor="#f3f4f6"
                                                    gap={1}
                                                >
                                                    <Text
                                                        fontSize={9}
                                                        color="$gray1000"
                                                        fontWeight="600"
                                                    >
                                                        Payment source
                                                    </Text>
                                                    <XStack justifyContent="space-between">
                                                        <Text
                                                            fontSize={9}
                                                            color="#be185d"
                                                        >
                                                            From minimums
                                                            (bills)
                                                        </Text>
                                                        <Text
                                                            fontSize={10}
                                                            fontFamily="monospace"
                                                            color="#be185d"
                                                        >
                                                            {formatCentsDisplay(
                                                                a.totalFromMinimums,
                                                            )}
                                                        </Text>
                                                    </XStack>
                                                    <XStack justifyContent="space-between">
                                                        <Text
                                                            fontSize={9}
                                                            color="#1d4ed8"
                                                        >
                                                            From goals pool
                                                        </Text>
                                                        <Text
                                                            fontSize={10}
                                                            fontFamily="monospace"
                                                            color="#1d4ed8"
                                                        >
                                                            {formatCentsDisplay(
                                                                a.totalFromGoals,
                                                            )}
                                                        </Text>
                                                    </XStack>
                                                </YStack>
                                            )}
                                        </YStack>
                                    )}
                            </YStack>
                        ))}
                    </XStack>
                </YStack>
            )}

            {/* Results table */}
            {simulationResult && (
                <SimulationResultsTable result={simulationResult} />
            )}
        </YStack>
    );
};

const webInputStyle: React.CSSProperties = {
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: 14,
    fontFamily: 'monospace',
    backgroundColor: '#fff',
    outline: 'none',
};

const nativeInputStyle = {
    padding: 8,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    fontSize: 14,
    fontFamily: 'monospace',
    backgroundColor: '#fff',
};

const jsonEditorStyle: StyleProp<TextStyle> = {
    fontFamily: 'monospace',
    fontSize: 12,
    lineHeight: 18,
    padding: 16,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    backgroundColor: '#fafafa',
    minHeight: 400,
    maxHeight: 600,
};
